import React, {
	forwardRef,
	HTMLAttributes,
	memo,
	ReactNode,
	useEffect,
	useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pascalcase from 'pascalcase';
import { TColor } from '../../type/color-type';
import { TIcons, TIconsSize } from '../../type/icons-type';

type IconModule = Record<string, React.ComponentType<any>>;

let svgIconModule: IconModule | null = null;
let svgIconPromise: Promise<IconModule> | null = null;
const loadSvgIcons = () => {
	if (svgIconModule) {
		return Promise.resolve(svgIconModule);
	}
	if (!svgIconPromise) {
		svgIconPromise = import('./svg-icons').then((mod) => {
			svgIconModule = mod as IconModule;
			return svgIconModule;
		});
	}
	return svgIconPromise;
};

let materialIconModule: IconModule | null = null;
let materialIconPromise: Promise<IconModule> | null = null;
const loadMaterialIcons = () => {
	if (materialIconModule) {
		return Promise.resolve(materialIconModule);
	}
	if (!materialIconPromise) {
		materialIconPromise = import('./material-icons').then((mod) => {
			materialIconModule = mod as IconModule;
			return materialIconModule;
		});
	}
	return materialIconPromise;
};

interface IRefWrapperProps extends Record<string, any> {
	children: ReactNode;
}
// @ts-ignore
const RefWrapper = forwardRef<HTMLSpanElement, IRefWrapperProps>(({ children }, ref) => {
	if (ref) {
		return (
			<span ref={ref} data-only-ref='true'>
				{children}
			</span>
		);
	}
	return children;
});
RefWrapper.displayName = 'RefWrapper';

interface IIconProps extends HTMLAttributes<HTMLSpanElement> {
	icon?: TIcons;
	className?: string;
	color?: TColor;
	size?: TIconsSize;
	forceFamily?: null | 'custom' | 'material';
}
const Icon = forwardRef<HTMLSpanElement, IIconProps>(
	({ icon, className, color, size, forceFamily, ...props }, ref) => {
		const IconName = pascalcase(icon);
		const [svgIcons, setSvgIcons] = useState<IconModule | null>(svgIconModule);
		const [materialIcons, setMaterialIcons] = useState<IconModule | null>(
			materialIconModule,
		);

		const ClassName = classNames(
			'svg-icon',
			{ [`svg-icon-${size}`]: size, [`text-${color}`]: color },
			className,
		);

		const isForceCustom = forceFamily === 'custom';
		const isForceMaterial = forceFamily === 'material';

		useEffect(() => {
			if (isForceMaterial) {
				return;
			}
			let isMounted = true;
			loadSvgIcons().then((mod) => {
				if (isMounted) {
					setSvgIcons(mod);
				}
			});
			return () => {
				isMounted = false;
			};
		}, [isForceMaterial]);

		useEffect(() => {
			if (isForceCustom) {
				return;
			}
			let isMounted = true;
			loadMaterialIcons().then((mod) => {
				if (isMounted) {
					setMaterialIcons(mod);
				}
			});
			return () => {
				isMounted = false;
			};
		}, [isForceCustom]);

		const SvgIconWrapper =
			svgIcons && typeof svgIcons[IconName] === 'function'
				? (svgIcons[IconName] as React.ComponentType<any>)
				: undefined;
		const MaterialWrapper =
			materialIcons && typeof materialIcons[IconName] === 'function'
				? (materialIcons[IconName] as React.ComponentType<any>)
				: undefined;

		if (SvgIconWrapper && (isForceCustom || !isForceMaterial)) {
			return (
				<RefWrapper ref={ref}>
					<SvgIconWrapper
						data-name={`SvgIcon--${IconName}`}
						className={classNames('svg-icon--custom', ClassName)}
						{...props}
					/>
				</RefWrapper>
			);
		}
		if (MaterialWrapper && (isForceMaterial || !isForceCustom)) {
			return (
				<RefWrapper ref={ref}>
					<MaterialWrapper
						data-name={`Material--${icon}`}
						className={classNames('svg-icon--material', ClassName)}
						{...props}
					/>
				</RefWrapper>
			);
		}
		return <span className={ClassName} />;
	},
);
Icon.propTypes = {
	icon: PropTypes.string.isRequired,
	className: PropTypes.string,
	color: PropTypes.oneOf([
		null,
		'primary',
		'secondary',
		'success',
		'info',
		'warning',
		'danger',
		'light',
		'dark',
	]),
	size: PropTypes.oneOf([
		null,
		'sm',
		'md',
		'lg',
		'2x',
		'3x',
		'4x',
		'5x',
		'6x',
		'7x',
		'8x',
		'9x',
		'10x',
	]),
	forceFamily: PropTypes.oneOf([null, 'custom', 'material']),
};
Icon.defaultProps = {
	className: undefined,
	color: undefined,
	size: null,
	forceFamily: null,
};
Icon.displayName = 'Icon';

export default memo(Icon);
